import { useTheme, Typography } from "@mui/material";
import { Card, Col, Row } from "v2/components/base/Flex/ui";
import { CabalWithShills } from "v2/components/ScrollableItem/GroupRowItem/hocs/withGroupRowItem";
import { Value } from "components/Value";
import { Circle } from "@mui/icons-material";
import { useMemo } from "react";

const GroupRowItem = ({
  cabal,
  rank,
  navigateTo,
  handleOnError,
}: {
  cabal: CabalWithShills;
  rank: number;
  navigateTo: () => void;
  handleOnError: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}) => {
  const { tgGroupId, imageUri, name, change = 0, shills = 0 } = cabal;

  const rankText = useMemo(() => {
    if (rank === 1) {
      return "1st";
    } else if (rank === 2) {
      return "2nd";
    }
    return "3rd";
  }, [rank]);

  return (
    <>
      <div id={`cabalId-${tgGroupId}`} />
      <GroupRowContainer navigateTo={navigateTo}>
        <Row
          section="group-item-info"
          type="bc"
          sx={{
            gap: "12px",
            maxWidth: "50%",
          }}>
          <Row
            section="group-image"
            type="ss"
            sx={{
              paddingY: "4px",
              width: "34px",
            }}>
            <GroupImage src={imageUri ?? ""} alt={name} onError={handleOnError} />
          </Row>
          <Col
            section="group-item-info-name"
            type="sc"
            sx={{
              maxWidth: "100%",
              gap: "6px",
              overflowX: "hidden",
            }}>
            <Row
              section="group-item-info-name"
              type="sc"
              sx={{
                gap: "6px",
                maxWidth: "100%",
                overflowX: "hidden",
                textOverflow: "ellipsis",
                marginRight: "auto",
              }}>
              <Typography
                variant="mobile-body-m-heavy"
                sx={{
                  maxWidth: "100%",
                  textOverflow: "ellipsis",
                  overflowX: "hidden",
                  whiteSpace: "nowrap",
                }}>
                {name}
              </Typography>
            </Row>
            <Row section="shill-change" type="cs" sx={{ marginRight: "auto" }}>
              <Col>
                <Typography variant="mobile-body-s" color="text.secondary">
                  +{Math.round(change)} Shills
                </Typography>
              </Col>
              {rank <= 3 ? (
                <>
                  <Col>
                    <Circle sx={{ width: "4px", height: "4px", margin: "6px" }} />
                  </Col>
                  <Col>
                    <Typography
                      variant="mobile-body-s"
                      color={rank === 1 ? "brand.primary" : "#FFD07E"}>
                      {rankText}
                    </Typography>
                  </Col>
                </>
              ) : null}
            </Row>
            {/* NOTE: D */}
            {/* {cabalCall.length ? (
              <Row section="group-item-info-calls" type="cs" sx={{ marginRight: "auto" }}>
                {cabalCall
                  .filter((call) => {
                    const xPerformance = call.latestPriceUsd / call.priceUsd;
                    const THRESHOLD = 0.01;
                    return !(xPerformance < 1 + THRESHOLD || call.marketCap <= 0);
                  })
                  .map((call, index) => {
                    return (
                      <Box
                        sx={{
                          width: "16px",
                          height: "16px",
                          transform: `translateX(-${50 * index + 0}%)`,
                          border: "1px solid #000000",
                          boxSizing: "border-box",
                          borderRadius: "100%",
                          background: "#FFFFFF",
                          marginTop: "-4px",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}>
                        <img src={call.iconUri} alt="1" width={"100%"} height={"100%"} />
                      </Box>
                    );
                  })}
                {totalCalls > MAX_CALL_SHOW ? (
                  <Typography
                    sx={{
                      width: "16px",
                      height: "16px",
                      transform: `translateX(-${50 * 5 - 70}%)`,
                      marginTop: "-4px",
                    }}
                    variant="body-sm">
                    <b>+{totalCalls - MAX_CALL_SHOW}</b>
                  </Typography>
                ) : null}
              </Row>
            ) : null} */}
          </Col>
        </Row>
        <Row
          section="group-item-info-shills"
          type="ec"
          sx={{
            gap: "12px",
          }}>
          <Value
            variant="mobile-display-s"
            color={rank === 1 ? "brand.primary" : "#FFD07E"}
            value={Math.round(shills)}
          />
          <Card.Badge>
            <Typography variant="mobile-body-s-heavy" sx={{ whiteSpace: "nowrap" }}>
              Shills
            </Typography>
          </Card.Badge>
        </Row>
      </GroupRowContainer>
    </>
  );
};

const GroupRowContainer = ({
  children,
  navigateTo,
}: {
  children: React.ReactNode;
  navigateTo: () => void;
}) => {
  const theme = useTheme();
  return (
    <Row
      type="bc"
      sx={{
        cursor: "pointer",
        padding: "8px",
        border: `1px solid ${theme.palette.surface.silver}33`,
        background: `${theme.palette.surface.silver}33`,
        borderRadius: theme.spacing(1.5),
        gap: "4px",
        maxWidth: "100%",
        minHeight: "80px",
      }}
      fullWidth
      onClick={navigateTo}>
      {children}
    </Row>
  );
};

const GroupImage = ({
  src,
  alt,
  onError,
}: {
  src: string;
  alt: string;
  onError: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}) => {
  const theme = useTheme();

  return (
    <img
      style={{
        width: "34px",
        height: "34px",
        borderRadius: "50%",
        border: `1px solid ${theme.palette.border.dark}`,
        objectFit: "cover",
        objectPosition: "center",
        background: theme.palette.surface.container,
      }}
      src={src}
      alt={alt}
      onError={onError}
    />
  );
};

export default GroupRowItem;
